/**
 * Talas analytics — fires events to BOTH:
 *   1. Google Analytics 4 (gtag) — funnel/cohort analysis in GA UI
 *   2. /api/analytics/event — self-hosted store powering /admin overview tiles
 *
 * Safe no-op when gtag isn't loaded or the API is unreachable. Never throws.
 */
import api from './api';

export const GA_MEASUREMENT_ID = 'G-XC4C3EEH7T';

// Persisted anonymous session id (rotates every 30 min of inactivity per industry norm).
const SESSION_KEY = 'talas_anon_session';
const SESSION_TTL_MS = 30 * 60 * 1000;

function getSessionId() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const [sid, ts] = raw.split('|');
      if (sid && ts && Date.now() - parseInt(ts, 10) < SESSION_TTL_MS) {
        sessionStorage.setItem(SESSION_KEY, `${sid}|${Date.now()}`);
        return sid;
      }
    }
    const sid = `sid_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    sessionStorage.setItem(SESSION_KEY, `${sid}|${Date.now()}`);
    return sid;
  } catch {
    return null;
  }
}

let _currentUserId = null;

function safeGtag(...args) {
  if (typeof window === 'undefined') return;
  if (typeof window.gtag !== 'function') return;
  try { window.gtag(...args); } catch { /* swallow */ }
}

function sendBeacon(payload) {
  // axios path — also works pre-login, no credentials required by the endpoint.
  // Failure swallowed; analytics must never break the app.
  api.post('/analytics/event', payload).catch(() => {});
}

/** Fire a custom event. params are arbitrary key/values per GA4 spec. */
export function track(eventName, params = {}) {
  safeGtag('event', eventName, params);
  sendBeacon({
    name: eventName,
    params,
    session_id: getSessionId(),
    user_id: _currentUserId || undefined,
    path: typeof window !== 'undefined' ? window.location.pathname + window.location.search : undefined,
    referrer: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
    ts: Date.now(),
  });
}

/** Manual SPA page_view — call on every route change. */
export function trackPageView(path) {
  safeGtag('event', 'page_view', {
    page_path: path,
    page_location: typeof window !== 'undefined' ? window.location.href : path,
    page_title: typeof document !== 'undefined' ? document.title : undefined,
  });
  sendBeacon({
    name: 'page_view',
    path,
    referrer: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
    session_id: getSessionId(),
    user_id: _currentUserId || undefined,
    ts: Date.now(),
  });
}

/** Tie a logged-in user to subsequent events (no PII beyond anonymous id). */
export function identify(userId, traits = {}) {
  _currentUserId = userId;
  safeGtag('config', GA_MEASUREMENT_ID, { user_id: userId });
  if (Object.keys(traits).length) {
    safeGtag('set', 'user_properties', traits);
  }
}
